import { inject, Injectable } from '@angular/core';
import { SolutionCaptureService } from '@examdojo/solution';
import isEqual from 'lodash/isEqual';
import {
  BehaviorSubject,
  concatMap,
  distinctUntilChanged,
  filter,
  map,
  merge,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { QuestionAttemptRealtimeBaseService } from '../../../question-v2/question-attempt-realtime-base.service';
import { FullscreenFlowService } from '../../../shared/fullscreen-flow';

enum QuestionAttemptEvent {
  DISMISS_DIALOG = 'DISMISS_DIALOG',
  SOLUTION_CAPTURE_DIALOG = 'SOLUTION_CAPTURE_DIALOG',
}

interface QuestionAttemptEventPayload {
  dismiss?: boolean;
  open?: boolean;
}

@Injectable()
export class QuestionAttemptRealtimePracticeService extends QuestionAttemptRealtimeBaseService {
  private readonly fullscreenFlowService = inject(FullscreenFlowService);
  private readonly solutionCaptureService = inject(SolutionCaptureService);

  readonly latestChannelMessageForSolutionCaptureEvent$$ = new BehaviorSubject<QuestionAttemptEventPayload | null>(
    null,
  );

  protected override setupListeners(): Observable<unknown> {
    return merge(this.listenForActiveAttemptStatusChange(), this.listenForQuestionAttemptDismissDialogEvent());
  }

  sendQuestionDismissDialogEvent() {
    const channelName = `question_attempt_event:${this.questionAttemptQuery.getActive()?.id}`;
    const event = QuestionAttemptEvent.DISMISS_DIALOG;
    const payload: QuestionAttemptEventPayload = { dismiss: true };

    return this.realtimeService.sendMessage(channelName, event, payload);
  }

  sendQuestionSolutionCaptureDialogEvent(open: boolean) {
    const channelName = `question_attempt_event:${this.questionAttemptQuery.getActive()?.id}`;
    const event = QuestionAttemptEvent.SOLUTION_CAPTURE_DIALOG;
    const payload: QuestionAttemptEventPayload = open ? { open: true } : { dismiss: true };

    if (isEqual(this.latestChannelMessageForSolutionCaptureEvent$$.value, payload)) {
      return of(null);
    }

    return this.realtimeService.sendMessage(channelName, event, payload);
  }

  private listenForActiveAttemptStatusChange() {
    return this.questionAttemptQuery.attempt$.pipe(
      map((attempt) => attempt?.id),
      filter(Boolean),
      distinctUntilChanged(),
      switchMap((attemptId) => this.listenToAttemptStatusChanges(attemptId)),
    );
  }

  private listenForQuestionAttemptDismissDialogEvent() {
    return this.questionAttemptQuery.attempt$.pipe(
      map((attempt) => attempt?.id),
      filter(Boolean),
      distinctUntilChanged(),
      switchMap((attemptId) =>
        this.realtimeService
          .listenBroadcastRealtime<QuestionAttemptEventPayload, QuestionAttemptEvent>(
            [{ event: QuestionAttemptEvent.DISMISS_DIALOG }, { event: QuestionAttemptEvent.SOLUTION_CAPTURE_DIALOG }],
            `question_attempt_event:${attemptId}`,
          )
          .pipe(
            withLatestFrom(this.platformService.activeStatusState$),
            filter(([_, isActive]) => isActive),
            map(([event]) => event),
            concatMap((payload) => {
              if (
                payload.event === QuestionAttemptEvent.DISMISS_DIALOG &&
                payload?.payload?.dismiss &&
                this.fullscreenFlowService.isOpen()
              ) {
                return this.fullscreenFlowService.dismiss();
              }

              if (payload.event === QuestionAttemptEvent.SOLUTION_CAPTURE_DIALOG) {
                this.latestChannelMessageForSolutionCaptureEvent$$.next(payload.payload);

                if (payload?.payload?.dismiss && this.solutionCaptureService.isDialogOpen()) {
                  this.latestChannelMessageForSolutionCaptureEvent$$.next(payload.payload);
                  this.solutionCaptureService.dismiss();
                  return of(false);
                }

                if (payload?.payload?.open && !this.solutionCaptureService.isDialogOpen()) {
                  this.questionAttemptService.setOpenCaptureDialogFlag(true);
                  return of(true);
                }
              }

              return of(false);
            }),
          ),
      ),
    );
  }
}
